/**
 * StepCarousel.tsx  (Step 4)
 *
 * The carousel viewer. Shows three possible states in sequence:
 *   1. Summoning animation (while PDF generates or re-renders)
 *   2. Error state (generation failed)
 *   3. PDF ready — toolbar + canvas/grid + action buttons
 *
 * The toolbar contains: view toggle, slide nav (single view only),
 * theme switcher, edit panel toggle, and download buttons.
 * Below the PDF: LinkedIn posting button and secondary navigation.
 */

import React from "react";
import PdfStrip from "../../components/PdfStrip";
import SummoningAnimation from "../../components/SummoningAnimation";
import type { CarouselThemeMeta, ContentSlide } from "./types";
import { primaryBtn, ghostBtn, stepHeading, stepSub, card } from "./sharedStyles";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  // Loading / error state
  isCarouselLoading: boolean;
  isRendering:       boolean;
  isRestructuring:   boolean;
  carouselError:     boolean;
  craftingMessage:   string;

  // PDF data
  carouselPdfDoc:    any;  // pdfjs Document object
  allThemePdfs:      Record<string, string>; // themeKey → base64 PDF
  activeTheme:       string;
  approvedThemes:    CarouselThemeMeta[];
  currentSlide:      number;
  totalSlides:       number;

  // D1 view controls
  viewMode:          "single" | "grid";
  panelOpen:         boolean;
  isMobile:          boolean;
  slideCountMin:     number;

  // Download / renderer
  carouselRenderer:  "html" | "pptx";
  carouselPptxBase64: string | null;

  // Refs
  carouselCanvasRef: React.RefObject<HTMLCanvasElement>;

  // Setters
  setCurrentSlide:   React.Dispatch<React.SetStateAction<number>>;
  setViewMode:       (m: "single" | "grid") => void;
  setActiveTheme:    (t: string) => void;
  setPanelOpen:      React.Dispatch<React.SetStateAction<boolean>>;

  // Callbacks
  onDownloadPdf:      () => void;
  onDownloadPptx:     () => void;
  onPrepareLinkedIn:  () => void;
  onChangeTemplate:   () => void;
  onNewThought:       () => void;
  onRemoveSlide:      (index: number) => void;
  onGridSlideClick:   (index: number) => void;
  /** Re-try generating the currently active tab that failed. */
  onRetryTemplate:    () => void;
  /** Template tab bar rendered right above the carousel card. Kept as ReactNode so
   *  all D2 tab logic stays in index.tsx; StepCarousel just positions it. */
  tabBar?:            React.ReactNode;
  /** Cached thumbnail data-URLs for the active tab — passed to PdfStrip to skip re-parse. */
  preRenderedThumbnails?: string[];
  /** Called by PdfStrip after a fresh parse so the parent can cache the URLs per tab. */
  onThumbsReady?:     (urls: string[]) => void;

  // ── Custom brand colour (PPTX only) ─────────────────────────────────────────
  /** Current hex value in the brand-colour input ('' when not set). */
  customBrandColor?:          string;
  /** Fired on every keystroke / colour-picker change — updates the input value only, no API call. */
  onCustomBrandColorChange?:  (hex: string) => void;
  /** Fired on Apply button click or Enter keypress — triggers the retheme API call. */
  onApplyCustomColor?:        () => void;
  /** Fired when user clicks a preset swatch on a PPTX template — triggers retheme via that preset's accent. */
  onPickPresetTheme?:         (theme: CarouselThemeMeta) => void;
  /** Fired on ✕ button — clears custom colour and reverts to base theme (no API call). */
  onClearCustomColor?:        () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const StepCarousel: React.FC<Props> = ({
  isCarouselLoading, isRendering, isRestructuring, carouselError, craftingMessage,
  carouselPdfDoc, allThemePdfs, activeTheme, approvedThemes, currentSlide, totalSlides,
  viewMode, panelOpen, isMobile, slideCountMin,
  carouselRenderer, carouselPptxBase64,
  carouselCanvasRef,
  setCurrentSlide, setViewMode, setActiveTheme, setPanelOpen,
  onDownloadPdf, onDownloadPptx, onPrepareLinkedIn,
  onChangeTemplate, onNewThought,
  onRemoveSlide, onGridSlideClick,
  onRetryTemplate, tabBar,
  preRenderedThumbnails, onThumbsReady,
  customBrandColor, onCustomBrandColorChange, onApplyCustomColor, onPickPresetTheme, onClearCustomColor,
}) => {
  // True when the PDF data has arrived but pdfjs hasn't parsed it yet (brief gap)
  const awaitingPdfParse =
    Object.keys(allThemePdfs).length > 0 &&
    !carouselPdfDoc &&
    !carouselError &&
    !isRestructuring;

  const showAnimation = isCarouselLoading || isRendering || awaitingPdfParse;
  const showViewer    = !isCarouselLoading && !isRendering && carouselPdfDoc && !carouselError;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* Page heading — only visible when the carousel is fully ready */}
      {showViewer && !isRestructuring && (
        <div>
          <p style={stepHeading}>Your carousel</p>
          <p style={stepSub}>Refine the content, switch views, or post to LinkedIn.</p>
        </div>
      )}

      {/* ── Template tab bar (from parent) + main content area ──
          Wrapped together so the tab bar sits flush above the card/animation
          with no extra gap between them. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {/* Tab bar — always visible on the carousel step */}
        {tabBar && <div style={{ marginBottom: "8px" }}>{tabBar}</div>}

        {/* ── Summoning animation (first load + re-renders) ── */}
        {showAnimation && <SummoningAnimation message={isRendering ? "Rendering your changes…" : craftingMessage} />}

        {/* ── Error state ── */}
        {carouselError && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#888" }}>
            <div style={{ fontSize: "24px", marginBottom: "12px" }}>⚠</div>
            <p style={{ fontWeight: 600, marginBottom: "8px", color: "#444" }}>Couldn't generate the carousel</p>
            <p style={{ fontSize: "13px", marginBottom: "20px" }}>Something went wrong. Retry this template or pick a different one.</p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <button style={primaryBtn} onClick={onRetryTemplate}>↺ Try again</button>
              <button style={ghostBtn}   onClick={onChangeTemplate}>← Pick another template</button>
            </div>
          </div>
        )}

        {/* ── PDF viewer + toolbar ── */}
        {showViewer && (
          <>
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <Toolbar
                viewMode={viewMode}
                currentSlide={currentSlide}
                totalSlides={totalSlides}
                panelOpen={panelOpen}
                isMobile={isMobile}
                carouselRenderer={carouselRenderer}
                carouselPptxBase64={carouselPptxBase64}
                setViewMode={setViewMode}
                setCurrentSlide={setCurrentSlide}
                setPanelOpen={setPanelOpen}
                onDownloadPdf={onDownloadPdf}
                onDownloadPptx={onDownloadPptx}
              />

              {/* ── Theme switcher — own row right above the carousel content ── */}
              {/* Show for HTML when ≥1 approved theme exists, OR always for PPTX (custom colour input). */}
              {(approvedThemes.length > 0 || carouselRenderer === "pptx") && (
                <div style={{
                  display:      "flex",
                  alignItems:   "center",
                  gap:          "6px",
                  padding:      "8px 12px",
                  borderBottom: "1px solid #E4E4E0",
                  flexWrap:     "wrap",
                }}>

                  {/* Preset swatches — HTML: instant PDF swap; PPTX: trigger retheme API */}
                  {approvedThemes.map(t => (
                    <button
                      key={t.key}
                      className={`d1-toolbar-btn${activeTheme === t.key && !customBrandColor ? " active" : ""}`}
                      style={activeTheme === t.key && !customBrandColor
                        ? { borderColor: t.accent, color: t.accent, background: `${t.accent}18` }
                        : {}}
                      onClick={() =>
                        carouselRenderer === "pptx" && onPickPresetTheme
                          ? onPickPresetTheme(t)   // PPTX: triggers retheme API with preset accent
                          : setActiveTheme(t.key)  // HTML: instant PDF swap (unchanged)
                      }
                    >
                      {t.label}
                    </button>
                  ))}

                  {/* Custom colour input — PPTX templates only */}
                  {carouselRenderer === "pptx" && (
                    <div style={{
                      display:    "flex",
                      alignItems: "center",
                      gap:        "6px",
                      marginLeft: approvedThemes.length > 0 ? "4px" : "0",
                    }}>
                      {/* Native colour picker — updates input value only, Apply button fires API */}
                      <input
                        type="color"
                        value={customBrandColor || "#5B4FE8"}
                        onChange={e => onCustomBrandColorChange?.(e.target.value)}
                        title="Pick brand colour"
                        style={{
                          width: "28px", height: "28px",
                          border: "1.5px solid #E4E4E0", borderRadius: "6px",
                          cursor: "pointer", padding: "1px",
                        }}
                      />
                      {/* Hex text input — Enter key also fires apply */}
                      <input
                        type="text"
                        placeholder="#DC2626"
                        value={customBrandColor ?? ""}
                        onChange={e => onCustomBrandColorChange?.(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") onApplyCustomColor?.(); }}
                        style={{
                          width: "76px", fontSize: "12px", padding: "4px 8px",
                          border: `1.5px solid ${customBrandColor ? "#5B4FE8" : "#E4E4E0"}`,
                          borderRadius: "6px", fontFamily: "inherit", outline: "none",
                        }}
                      />
                      {/* Apply button — only active when a valid 6-digit hex is entered */}
                      <button
                        onClick={onApplyCustomColor}
                        disabled={!/^#[0-9a-fA-F]{6}$/i.test(customBrandColor ?? "")}
                        className="d1-toolbar-btn"
                        style={{
                          opacity: /^#[0-9a-fA-F]{6}$/i.test(customBrandColor ?? "") ? 1 : 0.4,
                        }}
                      >
                        Apply
                      </button>
                      {/* Clear button — reverts to base theme, no API call */}
                      {customBrandColor && (
                        <button
                          onClick={onClearCustomColor}
                          title="Clear custom colour"
                          style={{
                            fontSize: "11px", color: "#aaa", background: "none",
                            border: "none", cursor: "pointer", padding: "0 2px",
                          }}
                        >✕</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Single slide canvas — always in the DOM so carouselCanvasRef is always
                  set and the canvas renders when carouselPdfDoc first becomes available
                  (while still in grid mode). Switching to single just makes it visible;
                  no extra render cycle needed. height:0 + overflow:hidden collapses it in
                  grid mode while keeping clientWidth correct for scale calculations. */}
              <div style={viewMode === "grid"
                ? { visibility: "hidden", height: 0, overflow: "hidden" } as React.CSSProperties
                : { position: "relative" }
              }>
                <canvas
                  ref={carouselCanvasRef}
                  style={{ display: "block", width: "100%", height: "auto" }}
                />
              </div>

              {/* Grid view — all slide thumbnails */}
              {viewMode === "grid" && (
                <div style={{ padding: "0 16px" }}>
                  <PdfStrip
                    base64Pdf={allThemePdfs[activeTheme]}
                    activeSlideIndex={currentSlide}
                    onSlideClick={onGridSlideClick}
                    onRemove={onRemoveSlide}
                    slideCountMin={slideCountMin}
                    preRenderedUrls={preRenderedThumbnails}
                    onThumbsReady={onThumbsReady}
                  />
                </div>
              )}
            </div>

            {/* ── Action buttons ── */}
            <ActionButtons
              isMobile={isMobile}
              onPrepareLinkedIn={onPrepareLinkedIn}
              onChangeTemplate={onChangeTemplate}
              onNewThought={onNewThought}
            />
          </>
        )}
      </div>  {/* end tab bar + content wrapper */}
    </div>
  );
};

// ── Toolbar ───────────────────────────────────────────────────────────────────

/** The PDF card's top bar: view toggle, slide counter, edit, downloads.
 *  Theme switcher has been moved to its own row below this toolbar, closer to the content. */
const Toolbar: React.FC<{
  viewMode:          "single" | "grid";
  currentSlide:      number;
  totalSlides:       number;
  panelOpen:         boolean;
  isMobile:          boolean;
  carouselRenderer:  "html" | "pptx";
  carouselPptxBase64: string | null;
  setViewMode:       (m: "single" | "grid") => void;
  setCurrentSlide:   React.Dispatch<React.SetStateAction<number>>;
  setPanelOpen:      React.Dispatch<React.SetStateAction<boolean>>;
  onDownloadPdf:     () => void;
  onDownloadPptx:    () => void;
}> = ({
  viewMode, currentSlide, totalSlides,
  panelOpen, isMobile, carouselRenderer, carouselPptxBase64,
  setViewMode, setCurrentSlide, setPanelOpen,
  onDownloadPdf, onDownloadPptx,
}) => (
  <div style={{
    display:     "flex",
    alignItems:  "center",
    gap:         "6px",
    padding:     "10px 12px",
    borderBottom: "1px solid #E4E4E0",
    background:  "#fff",
    flexWrap:    "wrap",
  }}>
    {/* View toggle */}
    <button
      className={`d1-toolbar-btn${viewMode === "single" ? " active" : ""}`}
      onClick={() => setViewMode("single")}
      title="Single slide view"
    >▭{!isMobile && " Single"}</button>
    <button
      className={`d1-toolbar-btn${viewMode === "grid" ? " active" : ""}`}
      onClick={() => setViewMode("grid")}
      title="Grid view"
    >⊞{!isMobile && " Grid"}</button>

    {/* Slide navigation (single view only) */}
    {viewMode === "single" && (
      <>
        <Divider />
        <button
          className="d1-toolbar-btn"
          style={{ padding: "5px 8px" }}
          onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
          disabled={currentSlide === 0}
        >‹</button>
        <span style={{ fontSize: "12px", color: "#444", fontWeight: 600, minWidth: "40px", textAlign: "center" }}>
          {currentSlide + 1}/{totalSlides}
        </span>
        <button
          className="d1-toolbar-btn"
          style={{ padding: "5px 8px" }}
          onClick={() => setCurrentSlide(s => Math.min(totalSlides - 1, s + 1))}
          disabled={currentSlide >= totalSlides - 1}
        >›</button>
      </>
    )}

    <span style={{ flex: 1 }} />

    {/* Edit panel toggle */}
    <button
      className={`d1-toolbar-btn${panelOpen ? " active" : ""}`}
      onClick={() => setPanelOpen(p => !p)}
      title="Edit & regenerate"
    >✏{!isMobile && " Edit"}</button>

    <Divider />

    {/* Download buttons */}
    <button className="d1-toolbar-btn" onClick={onDownloadPdf}>↓ PDF</button>
    {carouselRenderer === "pptx" && carouselPptxBase64 && (
      <button className="d1-toolbar-btn" onClick={onDownloadPptx}>↓ PPTX</button>
    )}
  </div>
);

// ── ActionButtons ─────────────────────────────────────────────────────────────

/** Row of action buttons below the PDF card. */
const ActionButtons: React.FC<{
  isMobile:           boolean;
  onPrepareLinkedIn:  () => void;
  onChangeTemplate:   () => void;
  onNewThought:       () => void;
}> = ({ onPrepareLinkedIn, onChangeTemplate, onNewThought }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    {/* Primary CTA: post to LinkedIn */}
    <button
      style={{
        ...primaryBtn,
        background:      "#0A66C2",
        justifyContent:  "center",
        width:           "100%",
        padding:         "14px 24px",
        fontSize:        "15px",
      }}
      onClick={onPrepareLinkedIn}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
      Prepare LinkedIn post
    </button>

    {/* Secondary navigation */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <button style={{ ...ghostBtn, color: "#aaa" }} onClick={onChangeTemplate}>← Change template</button>
      <button
        style={{ ...primaryBtn, background: "#0A0A0A", fontSize: "13px", padding: "10px 18px" }}
        onClick={onNewThought}
      >
        New thought ↺
      </button>
    </div>
  </div>
);

// ── Divider ───────────────────────────────────────────────────────────────────

const Divider: React.FC = () => (
  <div style={{ width: "1px", height: "16px", background: "#E4E4E0", margin: "0 2px" }} />
);

export default StepCarousel;
